import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import queryString from 'query-string'

import { IconCornerDownRight } from '@tabler/icons-react'
import {
  createNote,
  deleteNote,
  fetchAllNotesForCall,
  patchNote,
} from '@/reducers/notes/notes.actions'

import { updateMediaPlayer } from '@/reducers/media/media.redux'
import { NoteCard } from '@/components/notes/NoteCard'
import { NoteForm } from '@/components/notes/NoteForm'
import { allowAgentVisibleFor } from '@/components/notes/noteVisibility'
import { useOrgUsers } from '@/components/notes/useOrgUsers'

import { CALL_EXPLORER_TAB_ENUMS as enums, formatTime, updateCallExplorerParams } from './helpers'

import './CallNotesTab.scss'

const SOURCE_LABEL = {
  call: 'Call',
  message: 'Message',
  scorecard: 'Scorecard',
  scorecard_criteria_score: 'Criterion',
  task: 'Task',
}

// Source-chip text: the base label plus the owning entity's name when the
// for-call aggregate provides it (e.g. "Scorecard: Sales QA"). Call notes
// anchored to a moment append the timestamp instead.
const sourceLabel = (note) => {
  const base = SOURCE_LABEL[note.note_type] || 'Note'
  switch (note.note_type) {
    case 'scorecard':
      return note.scorecard_name ? `${base}: ${note.scorecard_name}` : base
    case 'scorecard_criteria_score':
      return note.criteria_name ? `${base}: ${note.criteria_name}` : base
    case 'task':
      return note.task_name ? `${base}: ${note.task_name}` : base
    case 'call':
      return note.call_moment_timestamp != null
        ? `${base} · ${formatTime(note.call_moment_timestamp)}`
        : base
    default:
      return base
  }
}

// Task notes open in their owning inbox, keyed by the task's type. The inbox
// reads `?id=<taskId>` and opens the drawer (type-agnostic), so an unknown /
// missing type falls back to the Quality inbox.
const TASK_INBOX_ROUTE = {
  coaching: '/coaching/inbox',
  compliance: '/compliance/inbox',
  qa_dispute: '/qa-copilot/inbox',
  qa_manual_score: '/qa-copilot/inbox',
}
const DEFAULT_TASK_INBOX_ROUTE = '/qa-copilot/inbox'

// The anchor a note belongs to, so edit/delete route to the right slice.
const anchorForNote = (note) => {
  switch (note.note_type) {
    case 'scorecard':
      return { noteType: 'scorecard', id: note.scorecard_id }
    case 'scorecard_criteria_score':
      return { noteType: 'scorecard_criteria_score', id: note.scorecard_criteria_score_id }
    case 'task':
      return { noteType: 'task', id: note.task_id }
    case 'message':
      return { noteType: 'message', id: note.message_id }
    case 'call':
    default:
      return { noteType: 'call', id: note.call_id }
  }
}

/**
 * Call Explorer "Comments" tab. A compose box for new call-level notes,
 * the call's own comments, then the union of every other note touching the
 * call (message, scorecard, criteria, task) from GET /api/notes/for-call/
 * {callId}.
 *
 * Each row carries a muted "↪ source" line that doubles as a jump:
 *   - scorecard  → QA Scores tab, scrolled to the scorecard card
 *   - criteria   → QA Scores tab, parent scorecard's editor
 *   - call+moment→ seeks the transcript/player to that moment
 *   - task       → the owning inbox with the task drawer open
 * Message notes (and plain call notes) have no jump target.
 */
export const CallNotesTab = ({ callId, organizationId, setOpenTab }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const orgUsers = useOrgUsers(organizationId)

  const notes = useSelector((state) => state.notes.forCall[callId] || [])
  const isLoading = useSelector((state) => state.notes.loading[`for-call:${callId}`] === true)

  const loadAggregate = () => dispatch(fetchAllNotesForCall({ callId, organizationId }))

  useEffect(() => {
    if (callId != null) {
      dispatch(fetchAllNotesForCall({ callId, organizationId }))
    }
  }, [callId, organizationId, dispatch])

  const handleCreate = async ({ noteText, visibility, mentionedUserIds }) => {
    await dispatch(
      createNote({
        noteType: 'call',
        id: callId,
        noteText,
        visibility,
        mentionedUserIds,
        organizationId,
      })
    )
    loadAggregate()
  }

  const handlePatch = async (noteId, fields, note) => {
    await dispatch(patchNote({ noteId, fields, anchor: anchorForNote(note), organizationId }))
    loadAggregate()
  }

  const handleDelete = async (noteId, note) => {
    await dispatch(deleteNote({ noteId, anchor: anchorForNote(note), organizationId }))
    loadAggregate()
  }

  // Scorecard note → land on the scorecard's card in the QA Scores list
  // (scrollToScorecardId), NOT the criteria editor. Clear scorecardId so a
  // prior criteria jump doesn't leave the editor open.
  const scrollToScorecard = (scorecardId) => {
    updateCallExplorerParams(
      location,
      history,
      { tab: enums.QA_SCORES, scrollToScorecardId: scorecardId },
      ['scorecardId']
    )
    setOpenTab(enums.QA_SCORES)
  }

  // Criteria note → open the parent scorecard's editor, where the criteria
  // (and their notes) live.
  const openScorecardEditor = (scorecardId) => {
    updateCallExplorerParams(location, history, { tab: enums.QA_SCORES, scorecardId }, [
      'scrollToScorecardId',
    ])
    setOpenTab(enums.QA_SCORES)
  }

  // Seek the left-panel player + transcript to the note's anchored moment.
  // The MediaPlayer/transcript live outside this panel but both read
  // media.mediaPlayer.hardSelectedEvent from redux (same path the Events
  // tab uses), so a dispatch is all that's needed.
  const jumpToMoment = (timestamp) => {
    dispatch(updateMediaPlayer({ hardSelectedEvent: { timestamp } }))
  }

  // Task note → navigate to the owning inbox with the task drawer open.
  const openTaskInInbox = (note) => {
    const route = TASK_INBOX_ROUTE[note.task_type] || DEFAULT_TASK_INBOX_ROUTE
    history.push({ pathname: route, search: queryString.stringify({ id: note.task_id }) })
  }

  // Returns the jump handler for a note, or null when it has no target yet.
  const getJumpHandler = (note) => {
    if (note.note_type === 'scorecard' && note.scorecard_id != null) {
      return () => scrollToScorecard(note.scorecard_id)
    }
    if (note.note_type === 'scorecard_criteria_score' && note.scorecard_id != null) {
      return () => openScorecardEditor(note.scorecard_id)
    }
    if (note.note_type === 'call' && note.call_moment_timestamp != null) {
      return () => jumpToMoment(note.call_moment_timestamp)
    }
    if (note.note_type === 'task' && note.task_id != null) {
      return () => openTaskInInbox(note)
    }
    return null
  }

  // One muted context line: "↪ <source>". The whole line is the jump when
  // the note has a target; otherwise it's plain muted text. Truncated.
  const renderSource = (note) => {
    const onJump = getJumpHandler(note)
    // Plain call notes already live in the call-comments section, so the
    // "Call" label is redundant — only show a line when there's a moment jump.
    if (note.note_type === 'call' && !onJump) {
      return null
    }
    const label = sourceLabel(note)

    return (
      <span className="call-notes__source" data-testid={`note-source-${note.id}`}>
        {onJump ? (
          <button
            type="button"
            className="call-notes__source-link"
            data-testid={`note-jump-${note.id}`}
            onClick={onJump}
            title={label}
          >
            <IconCornerDownRight size={13} />
            <span className="call-notes__source-text">{label}</span>
          </button>
        ) : (
          <span className="call-notes__source-static" title={label}>
            {label}
          </span>
        )}
      </span>
    )
  }

  const visibleNotes = notes.filter((note) => !note.deleted)
  // Direct call comments come first; everything else (scorecard, criteria,
  // task, message) drops into a labeled "other" section below.
  const callNotes = visibleNotes.filter((note) => note.note_type === 'call')
  const otherNotes = visibleNotes.filter((note) => note.note_type !== 'call')

  const renderNote = (note) => (
    <NoteCard
      key={`${note.note_type}:${note.id}`}
      note={note}
      organizationId={organizationId}
      orgUsers={orgUsers}
      onPatch={(noteId, fields) => handlePatch(noteId, fields, note)}
      onDelete={(noteId) => handleDelete(noteId, note)}
      source={renderSource(note)}
    />
  )

  return (
    <div className="call-notes" data-testid={`call-notes-${callId}`}>
      <div className="call-notes__section">
        <h3 className="call-notes__section-title">Add a Call Comment</h3>
        {/* Call-anchored note. Route through `allowAgentVisibleFor` so a
            future change to the anchor-allowlist doesn't have to chase
            this hardcoded call site — single source of truth for which
            anchors surface the Agent option lives in noteVisibility.js. */}
        <NoteForm
          organizationId={organizationId}
          orgUsers={orgUsers}
          mode="create"
          allowAgentVisible={allowAgentVisibleFor('call')}
          onSubmit={handleCreate}
        />
      </div>

      {isLoading && isEmpty(visibleNotes) && (
        <div className="call-notes__empty">Loading notes…</div>
      )}

      {!isEmpty(callNotes) && (
        <div className="call-notes__section">
          <h3 className="call-notes__section-title">Call Comments</h3>
          <div className="call-notes__list">{callNotes.map(renderNote)}</div>
        </div>
      )}

      {!isEmpty(otherNotes) && (
        <div className="call-notes__section">
          <h3 className="call-notes__section-title">Comments Associated to Call</h3>
          <div className="call-notes__list">{otherNotes.map(renderNote)}</div>
        </div>
      )}
    </div>
  )
}
